/**
 * GuestLoginPage — Hotelogram entry. Lobby-themed.
 */
import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppKit, useAppKitAccount } from '@reown/appkit/react';
import {
  Mail, UserPlus, Wallet, ArrowRight, Flame, Shield, Coins, ArrowLeft, Leaf,
} from 'lucide-react';

export default function GuestLoginPage() {
  const navigate = useNavigate();
  const { open } = useAppKit();
  const { address, isConnected } = useAppKitAccount();

  useEffect(() => {
    if (isConnected && address) {
      navigate('/guest-portal', { state: { walletAddress: address } });
    }
  }, [isConnected, address, navigate]);

  const openConnect = () => open({ view: 'Connect' });

  const ctas = [
    {
      key: 'email',
      icon: Mail,
      title: 'Sign in with email',
      body: 'We\u2019ll keep the wallet out of your way. A self-custody one is created quietly in the background.',
      tid: 'cta-email-login',
    },
    {
      key: 'create',
      icon: UserPlus,
      title: 'Create an account',
      body: 'New to getbitsy? Start here \u2014 email, phone, or social login, all work.',
      tid: 'cta-create-account',
    },
    {
      key: 'wallet',
      icon: Wallet,
      title: 'Connect a wallet',
      body: 'MetaMask, WalletConnect, Coinbase \u2014 bring your own. No new password to remember.',
      tid: 'cta-web3-connect',
    },
  ];

  const props = [
    { icon: Coins,  body: 'Earn $HERMES on every referral booking.' },
    { icon: Flame,  body: 'Flash rates: 2\u00d7\u20135\u00d7 multipliers on hand-picked stays.' },
    { icon: Shield, body: 'Self-custody wallet \u2014 we never hold your keys.' },
  ];

  return (
    <div className="lobby-theme min-h-screen flex flex-col" data-testid="guest-login-page">
      <div className="absolute inset-0 lobby-glow pointer-events-none" />
      <div className="lobby-grain" />

      {/* Nav */}
      <nav className="relative z-10 border-b border-[color:var(--l-line)]">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 text-[color:var(--l-cream-2)] hover:text-[color:var(--l-cream)] transition-colors" data-testid="hotelogram-back-home">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to home</span>
          </Link>
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[color:var(--l-copper)] flex items-center justify-center">
              <span className="l-display text-[color:var(--l-bg)] text-xs">b</span>
            </div>
            <span className="l-display text-[15px]">getbitsy</span>
          </Link>
        </div>
      </nav>

      {/* Main */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-5 py-16 sm:py-24">
        <div className="w-full max-w-2xl">
          <div className="text-center lobby-reveal">
            <div className="text-[13px] tracking-wide text-[color:var(--l-cream-3)] mb-3">Hotelogram</div>
            <h1 className="l-display text-4xl sm:text-5xl lg:text-6xl">
              Good taste,<br />
              <span className="text-[color:var(--l-copper)]">rewarded</span>.
            </h1>
            <p className="mt-5 text-[color:var(--l-cream-2)] max-w-md mx-auto leading-relaxed">
              Share the hotels you already love. Collect a little back in $HERMES when a friend books.
            </p>
          </div>

          {/* CTA stack */}
          <div className="mt-12 space-y-4">
            {ctas.map(({ key, icon: Icon, title, body, tid }, i) => (
              <button
                key={key}
                onClick={openConnect}
                data-testid={tid}
                className="l-card w-full text-left p-5 flex items-center gap-5 lobby-reveal"
                style={{ animationDelay: `${140 + i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-full bg-[color:var(--l-bg-3)] border border-[color:var(--l-line-2)] flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-[color:var(--l-copper)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="l-display text-lg">{title}</div>
                  <div className="text-[13px] text-[color:var(--l-cream-2)] mt-1 leading-relaxed">{body}</div>
                </div>
                <ArrowRight className="w-5 h-5 text-[color:var(--l-cream-3)] shrink-0" />
              </button>
            ))}
          </div>

          {/* Value props */}
          <div className="mt-12 pt-8 border-t border-[color:var(--l-line)] grid sm:grid-cols-3 gap-6">
            {props.map(({ icon: Icon, body }, i) => (
              <div key={i} className="flex items-start gap-3">
                <Icon className="w-4 h-4 text-[color:var(--l-copper)] mt-0.5 shrink-0" />
                <p className="text-[13px] text-[color:var(--l-cream-2)] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-[12px] text-center text-[color:var(--l-cream-3)]">
            By continuing, you agree to the getbitsy Terms of Service and Privacy Policy.
          </p>
        </div>
      </main>

      <footer className="relative z-10 border-t border-[color:var(--l-line)] py-6">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 flex items-center justify-center text-[12px] text-[color:var(--l-cream-3)]">
          <span className="inline-flex items-center gap-2">
            <Leaf className="w-3.5 h-3.5 text-[color:var(--l-copper)]" /> Hospitality, quietly rebuilt.
          </span>
        </div>
      </footer>
    </div>
  );
}
